import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LoginModal } from '../components/LoginModal';
import { useAuth } from '../hooks/useAuth';
import {
  fetchChoirSubscribers,
  sendChoirMessage,
  type ChoirSubscriber,
} from '../services/api';

export function ChoirAdminPage() {
  const { isAdminAuthenticated, loginForAdmin } = useAuth();

  const [subscribers, setSubscribers] = useState<ChoirSubscriber[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingPassword, setPendingPassword] = useState('');

  useEffect(() => {
    if (isAdminAuthenticated) {
      loadSubscribers();
    }
  }, [isAdminAuthenticated]);

  const loadSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchChoirSubscribers();
      setSubscribers(data);
    } catch {
      setStatusMessage('Failed to load subscribers.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoginSuccess = useCallback(
    async (password: string) => {
      const success = await loginForAdmin(password);
      if (success) {
        setPendingPassword(password);
        loadSubscribers();
      }
      return success;
    },
    [loginForAdmin, loadSubscribers]
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(subscribers.map((s) => s.id)));
  }, [subscribers]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleSend = useCallback(
    async (password: string) => {
      if (!message.trim() || selectedIds.size === 0) return;
      setSending(true);
      setStatusMessage('');
      const result = await sendChoirMessage(message, Array.from(selectedIds), password);
      setSending(false);
      if (result.success) {
        setStatusMessage(`Sent to ${result.sentCount} of ${selectedIds.size} recipients.`);
        setMessage('');
        setSelectedIds(new Set());
      } else {
        setStatusMessage(result.error || 'Failed to send messages.');
      }
    },
    [message, selectedIds]
  );

  const handleSendClick = useCallback(() => {
    if (!message.trim()) {
      setStatusMessage('Please enter a message.');
      return;
    }
    if (selectedIds.size === 0) {
      setStatusMessage('Please select at least one recipient.');
      return;
    }
    if (pendingPassword) {
      handleSend(pendingPassword);
    } else {
      setShowLoginModal(true);
    }
  }, [message, selectedIds, pendingPassword, handleSend]);

  const handleLoginAndSend = useCallback(
    async (password: string) => {
      const loginResult = await loginForAdmin(password);
      if (loginResult) {
        setPendingPassword(password);
        await handleSend(password);
        return true;
      }
      return false;
    },
    [loginForAdmin, handleSend]
  );

  if (!isAdminAuthenticated) {
    return (
      <>
        <LoginModal
          isOpen={true}
          onClose={() => {}}
          onLogin={handleLoginSuccess}
          title="Admin Login"
          description="Enter the admin password to manage choir subscribers."
        />
        <div className="page-container">
          <Link to="/choir" className="home-page__back-link">← Choir</Link>
          <h1>Choir Admin</h1>
          <p>Please log in to continue.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLoginAndSend}
        title="Confirm Password"
        description="Enter the password to send messages."
      />

      <div className="page-container">
        <Link to="/choir" className="home-page__back-link">← Choir</Link>
        <div className="page-header">
          <h1>Choir Admin</h1>
          <button className="btn-secondary" onClick={loadSubscribers} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Subscriber List */}
        <div className="choir-admin__section">
          <div className="choir-admin__section-header">
            <h2>Subscribers ({subscribers.length})</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-secondary" onClick={selectAll}>
                Select All
              </button>
              <button className="btn-secondary" onClick={deselectAll}>
                Deselect All
              </button>
            </div>
          </div>

          {subscribers.length === 0 ? (
            <p className="choir-page__empty">No subscribers yet.</p>
          ) : (
            <div className="choir-admin__subscriber-list">
              {subscribers.map((sub) => (
                <label key={sub.id} className="choir-admin__subscriber-row">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(sub.id)}
                    onChange={() => toggleSelect(sub.id)}
                  />
                  <span className="choir-admin__subscriber-name">
                    {sub.firstName} {sub.lastName}
                  </span>
                  <span className="choir-admin__subscriber-phone">{sub.phone}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Send Message */}
        <div className="choir-admin__section">
          <h2>Send Message</h2>
          <p className="choir-page__section-desc">
            {selectedIds.size} recipient{selectedIds.size !== 1 ? 's' : ''} selected.
          </p>
          <textarea
            className="choir-page__textarea"
            rows={5}
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <button
            className="btn-primary"
            onClick={handleSendClick}
            disabled={sending || selectedIds.size === 0 || !message.trim()}
          >
            {sending ? 'Sending...' : `Send to ${selectedIds.size} Recipient${selectedIds.size !== 1 ? 's' : ''}`}
          </button>
          {statusMessage && (
            <p className={`choir-page__status ${statusMessage.startsWith('Sent') ? 'choir-page__status--success' : 'choir-page__status--error'}`}>
              {statusMessage}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
